import React from 'react'
import { Badge, Box, Button, Card, CounterBadge, EmptyState, IconButton, Loader, Page, PopoverMenu, Table, TableActionCell, TextButton } from '@wix/design-system';
import { useInstanceContext } from '../context/InstanceContext';
import { useFirebaseQueryOptions } from '../hooks/firebaseHooks';
import { Collections, FREE_CAMPAING_LIMIT } from '../utils/shared/constants';
import { doc, updateDoc, where } from 'firebase/firestore';
import { Campaign, CampaignStatus } from '../models/shared/Campaign';
import CampaignSidePanel from '../components/CampaignSidePanel';
import * as Icons from '@wix/wix-ui-icons-common';
import { useFirestore } from 'reactfire';
import { useNavigation } from '../context/NavigationContext';
import CampaignAnalyticsHighligtsCard from '../components/CampaignAnalyticsHighligtsCard';
import { getMailto } from '../utils/mailto';
import { redirectToUpgrade } from '../utils/logic';
import TrialInfo from '../components/TrialInfo';

interface Props { }

function CampaignListPage(props: Props) {
    const { navigate } = useNavigation();
    const { instanceData, instanceId } = useInstanceContext();
    const [campaigns, , loaded] = useFirebaseQueryOptions<Campaign>(
        Collections.campaigns,
        { queryConstraints: [where('instanceId', '==', instanceData.instanceId)], skip: !instanceData.instanceId });
    const [showSidebar, setShowSidebar] = React.useState({
        show: false,
        campaign: null as Campaign | null,
    });
    const firestore = useFirestore();
    const [showArchived, setShowArchived] = React.useState(false);

    const deactivateActiveCampaigns = async () => {
        const activeCampaigns = campaigns.filter(c => c.status === 'active');

        for (const campaign of activeCampaigns) {
            await updateDoc(doc(firestore, `${Collections.campaigns}/${campaign.id}`), { status: 'inactive', updatedAt: new Date() });
        }
    }

    const togleActive = async (campaignId: string, newStatus: CampaignStatus) => {
        if (newStatus === 'active') {
            await deactivateActiveCampaigns();
        }

        await updateDoc(doc(firestore, `${Collections.campaigns}/${campaignId}`), { status: newStatus, updatedAt: new Date() });
    }

    const renderStatus = (campaign: Campaign) => {
        if (campaign.status === 'active') {
            return <Badge skin="success">Active</Badge>
        }
        if (campaign.status === 'archived') {
            return <Badge skin="warning">Archived</Badge>
        }
        return <Badge skin="general" type="transparent">Inactive</Badge>
    }

    const finalData = campaigns.filter(c => showArchived ? c.status === 'archived' : c.status !== 'archived');
    const activeCampaign = campaigns.find(c => c.status === 'active');
    const askForUpgrade = !instanceData.planId && campaigns.length >= FREE_CAMPAING_LIMIT;


    return <div className='overflow-hidden' style={{ overflow: 'hidden', position: 'relative' }}>
        <Page height="100vh">
            <Page.Header
                title="Customer Feedback"
                subtitle="Get insights from your customers and improve your business."
                actionsBar={
                    <div className='d-flex align-items-center gap'>

                        {askForUpgrade
                            ? <Button skin='premium' prefixIcon={<Icons.Favorite />}
                                onClick={() => redirectToUpgrade(instanceId)} >
                                Upgrade
                            </Button>
                            : <Button skin='standard' priority="secondary" prefixIcon={<Icons.Add />}
                                onClick={() => setShowSidebar({ show: true, campaign: null })} >
                                New Campaign
                            </Button>
                        }

                        <PopoverMenu
                            textSize="small"
                            placement='bottom-end'
                            triggerElement={
                                <IconButton priority="secondary">
                                    <Icons.MoreSmall />
                                </IconButton>
                            }
                        >
                            <PopoverMenu.MenuItem
                                text="Off-site collection"
                                prefixIcon={<Icons.QR />}
                                onClick={() => window.location.href = getMailto(instanceData.instanceId || '', 'offSiteCollection')}
                            />
                            <PopoverMenu.MenuItem
                                text="Notifications"
                                prefixIcon={<Icons.Notification />}
                                onClick={() => window.location.href = getMailto(instanceData.instanceId || '', 'notifications')}
                            />
                            <PopoverMenu.Divider />
                            <PopoverMenu.MenuItem
                                onClick={() => window.location.href = getMailto(instanceData.instanceId || '')}
                                text="Help"
                                prefixIcon={<Icons.HelpCircle />}
                            />
                        </PopoverMenu>
                    </div>
                }
            />
            <Page.Content>
                <Box gap={'24px'} direction='vertical'>
                    {activeCampaign && <CampaignAnalyticsHighligtsCard campaign={activeCampaign} />}


                    <Card>
                        <Card.Header
                            title="Campaigns"
                            subtitle="Configure customer feedback campaigns and review results."
                            suffix={<Button size="small" skin='standard'
                                priority={showArchived ? 'primary' : 'secondary'}
                                prefixIcon={<Icons.Archive />}
                                onClick={() => setShowArchived(!showArchived)}>
                                {showArchived ? 'Hide archived' : 'Show archived'}
                            </Button>}
                        />
                        <Card.Divider />
                        <Table
                            data={finalData}
                            columns={[
                                {
                                    title: 'Name',
                                    render: campaign => campaign.name,
                                },
                                {
                                    title: 'Responses',
                                    render: (campaign) => <TextButton onClick={() => navigate('responses', { id: campaign.id + '' })}>
                                        <CounterBadge skin='standard' >{campaign.responseCount}</CounterBadge>
                                    </TextButton>
                                },
                                {
                                    title: 'Status',
                                    render: renderStatus,
                                },
                                {
                                    title: 'Created Date',
                                    render: (campaign) => campaign.createdAt.toLocaleString(),
                                },
                                {
                                    title: '',
                                    render: (campaign) => {
                                        const isActive = campaign.status === 'active';
                                        if (campaign.status === 'archived') {
                                            return <TableActionCell size="small" primaryAction={{
                                                text: 'Unarchive',
                                                onClick: () => togleActive(campaign.id || '', 'inactive'),
                                            }} />
                                        }

                                        return <TableActionCell size="small"
                                            primaryAction={{
                                                text: isActive ? 'Deactivate' : 'Activate',
                                                icon: isActive ? <Icons.Dismiss /> : <Icons.Confirm />,
                                                onClick: () => togleActive(campaign.id || '', isActive ? 'inactive' : 'active'),
                                            }}
                                            secondaryActions={[
                                                {
                                                    text: 'Edit',
                                                    icon: <Icons.Edit />,
                                                    onClick: () => setShowSidebar({ show: true, campaign: campaign }),
                                                },
                                                {
                                                    text: 'Responses',
                                                    icon: <Icons.DataCollection />,
                                                    onClick: () => navigate('responses', { id: campaign.id || '' })
                                                },
                                                {
                                                    divider: true,
                                                },
                                                {
                                                    text: 'Archive',
                                                    icon: <Icons.Archive />,
                                                    skin: 'destructive',
                                                    onClick: () => togleActive(campaign.id || '', 'archived'),
                                                },
                                            ]}
                                            numOfVisibleSecondaryActions={1}
                                        />
                                    }

                                },
                            ]}
                        />

                        {!loaded && <div className='center m-2'>
                            <Loader text="Loading campaigns" />
                        </div>}
                        {loaded && !finalData.length && <EmptyState
                            title="Add Campaign"
                            subtitle="Start collecting insights from your customers by creating a new campaign."
                        >
                            <TextButton prefixIcon={<Icons.Add />}
                                onClick={() => setShowSidebar({ show: true, campaign: null })}>
                                Add Campaign</TextButton>
                        </EmptyState>}

                        <div className='m-2' />
                    </Card>

                    <TrialInfo />
                </Box>
            </Page.Content>

        </Page>
        <CampaignSidePanel
            deactivateActiveCampaigns={deactivateActiveCampaigns}
            show={showSidebar.show} comapaign={showSidebar.campaign}
            onClose={() => setShowSidebar({ show: false, campaign: null })}
        />
    </div>
}

export default CampaignListPage
